import Box from '@zenbusiness/zui/Box';
import superstyled from '@zenbusiness/zui/styles/superstyled';

export const DynamicRendererRoot = superstyled({
  name: 'DynamicRenderer',
  slot: 'Root',
  tag: Box,
  styles: ({ theme, ischatmode }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    flex: 1, // fill the parent height
    minHeight: 0, // allow shrinking inside flex
    overflowY: 'auto',
    scrollBehavior: 'smooth',
    position: 'relative',
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    ...(!ischatmode && {
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2)
      },
      padding: theme.spacing(4, 4, 0, 4)
    })
  })
});

export const DynamicRendererContainer = superstyled({
  name: 'DynamicRenderer',
  slot: 'Container',
  tag: Box,
  propsNeeded: ['centerGreeting'],
  styles: ({ centerGreeting = false }) => ({
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',

    // Vertical centering for greeting (smooth transition)
    minHeight: centerGreeting ? 'calc(100dvh - 18rem)' : 'auto',
    justifyContent: centerGreeting ? 'center' : 'flex-start',
    alignItems: centerGreeting ? 'center' : 'stretch',
    textAlign: centerGreeting ? 'center' : 'left'
  })
});

export const DynamicRendererInstructionGroup = superstyled({
  name: 'DynamicRenderer',
  slot: 'InstructionGroup',
  tag: Box,
  propsNeeded: ['fullWidth', 'centerGreeting'],
  styles: ({ theme, fullWidth = false, centerGreeting = false }) => ({
    // Default: always use flexbox column
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    margin: '0 auto',
    maxWidth: fullWidth ? '100%' : '35rem',
    width: fullWidth ? '100%' : 'min(100vw - 2rem, 35rem)',

    // Only when centered AND there are 5+ children, switch to grid for 2x2 button layout
    ...(centerGreeting && {
      // Emotion SSR warning: prefer :first-of-type over :first-child
      '& > .DynamicRenderer-componentWrapper:first-of-type': {
        fontSize: '2rem',
        fontWeight: 'bold',
        gridColumn: '1 / -1'
      },

      // Emotion SSR warning: prefer :nth-of-type over :nth-child
      '&:has(.DynamicRenderer-componentWrapper:nth-of-type(4))': {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        // Add for mobile
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr'
        },

        // In the 7-item layout, force the first 3 *message* items to span both columns.
        // Defensive: only apply when the wrapper actually contains a Message component.
        '& > .DynamicRenderer-componentWrapper:nth-of-type(-n+3):has(.Message-root)': {
          gridColumn: '1 / -1'
        },

        // Ensure prompt buttons fill their grid cell and center their content.
        '& .Button-root': {
          width: '100%',
          justifyContent: 'center',
          maxWidth: '100%',
          height: '100%'
        },
        '& .ZuiButton-content': {
          width: '100%',
          justifyContent: 'center',
          textAlign: 'center'
        },

        // ButtonPrompt wraps the button with a stable class name (ButtonContainer-root / ButtonContainer-inner).
        // Override the default maxWidth="85%" so chips can fill the grid cell.
        '& .ButtonContainer-root': {
          width: '100%',
          height: '100%'
        },
        '& .ButtonPromptInner-inner': {
          maxWidth: '100%',
          width: '100%',
          height: '100%'
        }
      }
    })
  })
});

export const DynamicRendererComponentWrapper = superstyled({
  name: 'DynamicRenderer',
  slot: 'ComponentWrapper',
  tag: Box,
  styles: () => ({
    scrollMarginTop: '5rem'
  })
});

export const DynamicRendererBottomSentinel = superstyled({
  name: 'DynamicRenderer',
  slot: 'BottomSentinel',
  tag: Box,
  styles: ({ theme }) => ({
    height: '10px',
    width: '100%',
    marginBottom: theme.spacing(2)
  })
});
